import { ArrowLeftOutlined, FilePdfFilled, LoadingOutlined } from "@ant-design/icons";
import { Button, Card, Empty, List, message, Skeleton, Table, Tabs, Tag, Tooltip } from "antd";
import React, { Fragment, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChooseOrView, COMPARISON_COLORS, ComparisonSlot, CurrentOrCompare } from ".";
import { SessionContext } from "../../App";
import SurveyCampaignStatusTag from "../../components/SurveyCampaignStatusTag";
import {
  GetReportRequest,
  GetReportRequestLayout,
  GetSurveyCampaign360TeamReportRequest,
  GetSurveyCampaignBasicInfoResponse,
  GetSurveyCampaignGroupReportResponse,
  GetSurveyCampaignsResponse,
} from "../../constants/apiRequestResponse";
import TEST_IDS from "../../constants/cypressTestIds";
import { COLORS } from "../../constants/misc";
import { FeatureFlagStatus, FeatureFlagTemplateKey } from "../../constants/types";
import { getReport, getSurveyCampaignBasicInfo, getSurveyCampaigns } from "../../services/api";
import { FeedbackGroupQuestionReportKeys } from "../../services/i8tn/FeedbackReportView/keys";
import { getQueryParam, parseDate, setQueryParam } from "../../util";
interface Prop {
  report?: GetSurveyCampaignGroupReportResponse;
}

export default function FeedbackTeamView(props: Prop) {
  const context = useContext(SessionContext);
  const [teamReport, setTeamReport] = useState("");
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isTeamReportLoading, setIsTeamReportLoading] = useState(false);
  const [isPrintAsPDFLoading, setIsPrintAsPDFLoading] = useState(false);
  const [
    getSurveyCampaignBasicInfoResponse,
    setGetSurveyCampaignBasicInfoResponse,
  ] = useState<GetSurveyCampaignBasicInfoResponse | undefined>();
  const [team, setTeam] = useState<string>(getQueryParam("team") || "");
  const [comparedTo, setComparedTo] = useState<string | undefined>(getQueryParam("team.comparedTo") || undefined);
  const [comparisonReport, setComparisonReport] = useState("");
  const [isComparisonLoading, setIsComparisonLoading] = useState(false);
  const [chooseOrView, setChooseOrView] = useState<ChooseOrView | undefined>(getQueryParam("team.chooseOrView") as ChooseOrView || undefined)
  const [currentOrCompare, setCurrentOrCompare] = useState<CurrentOrCompare | undefined>(getQueryParam('team.currentOrCompare') as CurrentOrCompare || undefined)
  const [
    getSurveyCampaignsResponse,
    setGetSurveyCampaignsResponse,
  ] = useState<GetSurveyCampaignsResponse | undefined>();

  const { t } = useTranslation();

  const surveyCampaignId = getQueryParam("surveyCampaignId");

  useEffect(() => {
    if (!context?.company?._id) return;

    getSurveyCampaigns({
      surveyTemplateId: props.report?.surveyCampaign?.surveyTemplateId,
      createdBefore: props.report?.surveyCampaign?.createdAt
    })
      .then((res) => {
        if (!res.success) throw new Error(res.message);
        setGetSurveyCampaignsResponse(res);
      })
      .catch((err) => {
        message.error(err.message);
      });
  }, [context?.company?._id, surveyCampaignId, props.report]);

  useEffect(() => {
    try {
      setIsLoading(true);
      const req: GetSurveyCampaign360TeamReportRequest = {
        surveyCampaignId: surveyCampaignId,
      };
      getSurveyCampaignBasicInfo(req)
        .then((res) => {
          if (!res.success) throw new Error(res.message);
          setGetSurveyCampaignBasicInfoResponse(res);
        })
        .catch((err) => {
          message.error(err);
          setIsError(true);
        })
        .finally(() => setIsLoading(false));

      if (team) {
        setIsTeamReportLoading(true);
        const req: GetReportRequest = {
          surveyCampaignId: surveyCampaignId || "",
          format: "html",
          layout: GetReportRequestLayout.Team,
          team: team,
        };
        getReport(req)
          .then((res) => {
            setTeamReport(res?.data);
          })
          .catch((err) => {
            message.error(err);
          })
          .finally(() => setIsTeamReportLoading(false));
      }
    } catch (err) {
      message.error(
        typeof err === "string"
          ? err
          : typeof err === "object"
            ? err?.toString()
            : t(FeedbackGroupQuestionReportKeys.feedbackReport_somethingWentWrong)
      );
      setIsError(true);
    }
    setIsLoading(false);
  }, [surveyCampaignId, team, t]);

  useEffect(() => {
    if (!comparedTo) return;

    setIsComparisonLoading(true);
    const req: GetReportRequest = {
      surveyCampaignId: surveyCampaignId || "",
      format: "html",
      layout: GetReportRequestLayout.Team,
      comparedTo: comparedTo,
      team: team,
    };
    getReport(req)
      .then((res) => {
        setComparisonReport(res?.data);
      })
      .catch((err) => {
        setComparisonReport(err);
      })
      .finally(() => setIsComparisonLoading(false));
  }, [comparedTo, surveyCampaignId, team]);

  const onExportPDF = () => {
    setIsPrintAsPDFLoading(true);
    const req: GetReportRequest = {
      surveyCampaignId: surveyCampaignId || "",
      format: "pdf",
      layout: GetReportRequestLayout.Team,
      team: team,
      comparedTo: comparedTo
    };
    getReport(req)
      .catch((err) => {
        message.error(err);
      })
      .finally(() => setIsPrintAsPDFLoading(false));
  };

  if (isError) {
    return (
      <div>
        <h1>{t(FeedbackGroupQuestionReportKeys.feedbackReport_oopsInvalidSurveyCampaign)}</h1>
      </div>
    );
  }

  if (isLoading) {
    return <Skeleton active />;
  }

  const columns = [
    {
      title: <>{t(FeedbackGroupQuestionReportKeys.feedbackReport_teamTableHeader)}</>,
      width: 200,
      sorter: (
        a: { key: number; team: string },
        b: { key: number; team: string }
      ) => a.team.localeCompare(b.team),
      render: (rowData: { team: string; numberOfParticipants: number }) =>
        `${rowData.team} (${rowData.numberOfParticipants})`,
    },
    {
      title: (
        <>
          {t(
            FeedbackGroupQuestionReportKeys.feedbackIndividualView_actionsTableHeader
          )}
        </>
      ),
      render: (rowData: { team: string }) => {
        return (
          <Button
            type="link"
            onMouseEnter={() => {
              // warm up network cache
              const req: GetReportRequest = {
                surveyCampaignId: surveyCampaignId || "",
                format: "html",
                layout: GetReportRequestLayout.Team,
                team: rowData.team,
              };
              getReport(req)
            }}
            onClick={() => {
              setQueryParam("team.currentOrCompare", "")
              setQueryParam("team.comparedTo", "")
              setComparedTo("")
              setQueryParam("team", rowData.team);
              setTeam(rowData.team);

              const req: GetReportRequest = {
                surveyCampaignId: surveyCampaignId || "",
                format: "html",
                layout: GetReportRequestLayout.Team,
                team: rowData.team,
              };
              getReport(req)
                .then((res) => {
                  setTeamReport(res?.data);
                })
                .catch((err) => {
                  message.error(err);
                })
            }}
          >
            {t(FeedbackGroupQuestionReportKeys.feedbackIndividualView_viewReportLink)}
          </Button>
        );
      },
    },
  ];

  const dataSource =
    getSurveyCampaignBasicInfoResponse?.participantTeams?.map((t, i) => ({
      key: i,
      team: t.team,
      numberOfParticipants: t.numberOfParticipants,
    })) || [];

  const renderReportContent = (reportHtml: string) => {
    if (!team) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t(FeedbackGroupQuestionReportKeys.feedbackReport_selectTeamFromLeft)}
        />
      );
    }

    return <div dangerouslySetInnerHTML={{ __html: reportHtml }}></div>;
  };


  const isEnableBehavioralChangeFeature = context?.featureFlags?.find((ff) => {
    return (
      ff.key === FeatureFlagTemplateKey.ENABLE_BEHAVIORAL_CHANGE &&
      ff.status === FeatureFlagStatus.ENABLED
    );
  })
    ? true
    : false;

  if (isEnableBehavioralChangeFeature) {

    return (
      <div style={{ display: "flex", width: "100%", justifyContent: "space-around" }}>
        <Card style={{ width: "30%", minWidth: 400, overflow: "auto" }}>
          <Table columns={columns} dataSource={dataSource} pagination={false} />
        </Card>
        <Card style={{ width: "65%" }}>
          {team ? (
            <>
              <Tabs
                activeKey={currentOrCompare}
                onChange={key => {
                  setQueryParam('team.currentOrCompare', key)
                  setCurrentOrCompare(key as CurrentOrCompare)
                }}
              >
                <Tabs.TabPane tab={t(FeedbackGroupQuestionReportKeys.feedbackReport_currentReportTab)} key="current">
                  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
                    {isPrintAsPDFLoading ? (
                      <LoadingOutlined style={{ fontSize: 24 }} />
                    ) : (
                      <Tooltip title={t(FeedbackGroupQuestionReportKeys.feedbackGroupQuestionReport_downloadAsPDFTooltip)}>
                        <FilePdfFilled
                          data-testid={TEST_IDS.REPORT_PAGE.team_download_as_pdf_button_id}
                          onClick={() => onExportPDF()}
                          style={{ fontSize: 24, cursor: "pointer" }}
                        />
                      </Tooltip>
                    )}
                  </div>
                  {renderReportContent(teamReport)}
                </Tabs.TabPane>
                <Tabs.TabPane
                  tab={
                    <span>
                      {t(FeedbackGroupQuestionReportKeys.feedbackReport_behaviouralChangeReportTab)}
                      <Tag color="green" style={{ marginLeft: 4 }}>
                        {t(FeedbackGroupQuestionReportKeys.feedbackReport_newTag)}
                      </Tag>
                    </span>
                  }
                  key="compare"
                >
                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    {/* Past vs Present slots - shared between both views */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 40px 1fr", gap: 8, alignItems: "center" }}>
                      <ComparisonSlot
                        pastOrPresent="past"
                        surveyCampaign={getSurveyCampaignsResponse?.surveyCampaigns.find(sc => sc._id === comparedTo)}
                        showClear={chooseOrView === "choose"}
                        team={team}
                        onClear={() => {
                          setComparedTo(undefined);
                          setQueryParam("team.comparedTo", "");
                        }}
                      />
                      <div style={{ textAlign: "center", fontSize: 12, color: "#bfbfbf", fontWeight: 500 }}>{t(FeedbackGroupQuestionReportKeys.feedbackReport_vsLabel)}</div>
                      <ComparisonSlot
                        pastOrPresent="present"
                        surveyCampaign={props.report?.surveyCampaign}
                        showClear={false}
                        team={team}
                      />
                    </div>

                    {chooseOrView === "view" ? (
                      // Results view
                      <>
                        {/* Action bar */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: "8px 0",
                          borderBottom: "1px solid #f0f0f0",
                        }}>
                          <Button
                            type="link"
                            icon={<ArrowLeftOutlined />}
                            onClick={() => {
                              setChooseOrView("choose");
                              setQueryParam("team.chooseOrView", "choose");
                            }}
                          >
                            {t(FeedbackGroupQuestionReportKeys.feedbackReport_changeSelectionButton)}
                          </Button>
                          {isPrintAsPDFLoading ? (
                            <LoadingOutlined style={{ fontSize: 24 }} />
                          ) : (
                            <Tooltip title={t(FeedbackGroupQuestionReportKeys.feedbackGroupQuestionReport_downloadAsPDFTooltip)}>
                              <FilePdfFilled
                                data-testid={TEST_IDS.REPORT_PAGE.team_download_as_pdf_button_id}
                                onClick={() => onExportPDF()}
                                style={{ fontSize: 24, cursor: "pointer" }}
                              />
                            </Tooltip>
                          )}
                        </div>

                        {isComparisonLoading ? (
                          <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
                            <LoadingOutlined spin={true} style={{ fontSize: 32, color: COLORS.PRIMARY_COLOR }} />
                          </div>
                        ) : (
                          <div dangerouslySetInnerHTML={{ __html: comparisonReport }}></div>
                        )}
                      </>
                    ) : (
                      // Picker view
                      <>
                        {/* Campaign list */}
                        <List
                            dataSource={getSurveyCampaignsResponse?.surveyCampaigns.filter((sc) => sc._id !== surveyCampaignId)}
                          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t(FeedbackGroupQuestionReportKeys.feedbackReport_noPreviousCampaignsFound)} /> }}
                          renderItem={(c) => (
                            <List.Item
                              data-testclass='compareto-survey-campaigns-list'
                              key={c._id}
                              onClick={() => {
                                setQueryParam("team.comparedTo", c._id);
                                setComparedTo(c._id); // Preload the comparison report
                              }}
                              style={{
                                cursor: "pointer",
                                background: comparedTo === c._id ? COMPARISON_COLORS.past.background : undefined,
                                paddingLeft: 16,
                                paddingRight: 16,
                              }}
                            >
                              <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <span>{c.name}</span>
                                  <SurveyCampaignStatusTag
                                    status={c.status}
                                  />
                                </div>
                                <p style={{ color: "grey" }}>{parseDate(c.createdAt)}</p>
                              </div>
                            </List.Item>
                          )}
                        />

                        {/* Compare button */}
                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                          <Button
                            type="primary"
                            disabled={!comparedTo}
                            onClick={() => {
                              setQueryParam("team.chooseOrView", "view");
                              setChooseOrView("view")
                            }}
                          >
                            {t(FeedbackGroupQuestionReportKeys.feedbackReport_compareButton)}
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                </Tabs.TabPane>
              </Tabs>
            </>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t(FeedbackGroupQuestionReportKeys.feedbackReport_selectTeamFromLeft)}
            />
          )}
        </Card>
      </div>
    );
  } else {
    return (
      <div
        style={{
          display: "flex",
          width: "100%",
          justifyContent: "space-around",
        }}
      >
        <Card
          style={{
            width: "30%",
            minWidth: 400,
            overflow: "scroll",
          }}
        >
          <Table columns={columns} dataSource={dataSource} pagination={false} />
        </Card>
        <Card
          style={{
            width: "70%",
          }}
        >
          {isTeamReportLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <LoadingOutlined style={{ fontSize: 30 }} />
            </div>
          ) : team ? (
            <Fragment>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  width: "100%",
                  gap: 20,
                  marginBottom: 50,
                }}
              >
                {isPrintAsPDFLoading ? (
                  <LoadingOutlined style={{ fontSize: 30 }} />
                ) : (
                  <Tooltip
                    title={t(
                      FeedbackGroupQuestionReportKeys.feedbackGroupQuestionReport_downloadAsPDFTooltip
                    )}
                  >
                    <FilePdfFilled
                      data-testid={
                        TEST_IDS.REPORT_PAGE.team_download_as_pdf_button_id
                      }
                      onClick={(e) => {
                        onExportPDF();
                      }}
                      style={{ fontSize: 30 }}
                    />
                  </Tooltip>
                )}
              </div>

              <div dangerouslySetInnerHTML={{ __html: teamReport }}></div>
            </Fragment>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t(FeedbackGroupQuestionReportKeys.feedbackReport_selectTeamFromLeft)}
            />
          )}
        </Card>
      </div>
    );
  }
}
